import {
  ArrowLeft, Ban, CalendarClock, Copy, FileSignature, FileText, Link2, Mail, MessagesSquare, Mic, MoreHorizontal, Pause, Pencil, PhoneCall, PhoneOff, Play, RotateCcw, StickyNote, Tag, Trash2, Undo2,
} from "lucide-react";
import * as React from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge, ToneDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, Tooltip } from "@/components/ui/misc";
import { OverflowTabsList } from "@/components/ui/overflow-tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { AccidentCard, AccidentMark } from "@/features/accident";
import { BlockCallerDialog } from "@/features/block-caller";
import { CallPager, DirectionIcon } from "@/features/call-report";
import { CallMarkBadge } from "@/features/call-mark";
import { CallTypeChip, knownCallType } from "@/features/call-type";
import { ContactAppointmentsTab } from "@/features/contact-appointments";
import { AddToCampaign } from "@/features/contact-enlist";
import { NextCallCard } from "@/features/contact-follow-up";
import { ContactFormDialog } from "@/features/contact-form";
import { ContactPager } from "@/features/contact-pager";
import { ContactReviewFlags } from "@/features/contact-review-flags";
import { BookCallbackDialog } from "@/features/book-callback";
import { ContactOutcomeDialog } from "@/features/contact-outcome";
import { FirmOptions } from "@/features/firm-options";
import { ContactSmsTab } from "@/features/contact-sms";
import { ContactNotes, ContactTimeline } from "@/features/contact-timeline";
import { ContractHistoryCard } from "@/features/contract-history";
import { ContactTranscripts } from "@/features/contact-transcripts";
import { EmergencyContactCard } from "@/features/emergency-contact";
import { DeleteLeadDialog } from "@/features/lead-delete";
import { RecordedOutcome, useOutcomeLook } from "@/features/outcome-look";
import { CallingWindowLine, ContactDetailsPanel } from "@/features/record-details";
import { SamePhoneRecords } from "@/features/same-phone";
import { SendContractDialog } from "@/features/send-contract";
import { accidentSections } from "@/lib/accident";
import { untilPhrase } from "@/lib/blocked-callers";
import { useBlockOn } from "@/lib/blocked-callers-api";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { callDue, seesNextCall } from "@/lib/cadence";
import { US_TIMEZONES } from "@/lib/campaign";
import { placeFromState } from "@/lib/contact-pager";
import { callOutcome, talkShown } from "@/lib/call-outcome";
import {
  contactName, dialDetail, dialText, inSentence, nextText, reopenStages, reopenSummary, stageText,
} from "@/lib/contacts";
import {
  contactChannelBadge, contactChannelLabel, contactChannelOf, languageName, localTime, textFirstEnds,
} from "@/lib/lead-card";
import { sourceText } from "@/lib/opportunities";
import { isOutsideNumber } from "@/lib/phone-number";
import {
  CALLS_PAGE_SIZE, useContact, useContactCalls, useContactEmailCompose, useContactFields, useContactOutcome, useContractCheck, useFirms,
  useLeadAccident, useLeadCadence, useLeadCadenceAction, useReopenLead, useUpdateContact,
} from "@/lib/queries";
import { fieldText, keyAnswers, shownSections } from "@/lib/record-fields";
import { SAME_PHONE_CAVEAT } from "@/lib/same-phone";
import { SAME_PHONE_CARD_ID } from "@/lib/seen-before";
import { type ContractAsk, askedMessage, contractAsk } from "@/lib/send-contract";
import {
  atLeast, type Cdr, type ContractField, type LeadCard,
  seesCampaigns,
} from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDateTime, formatDuration, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { CallDetailDialog } from "@/routes/calls";
import { LeadTasks } from "@/features/task-list";
import { ownersKnown } from "@/lib/tasks";
import { DialNumberButton } from "@/softphone/dial-number";
import { SeenBeforeTag, useSeenBeforePage } from "@/softphone/seen-before";

/**
 * The contact record (ADR-044): the header and the record's details across the page, the timeline and
 * the other activity in a narrow panel down the right edge (ACL-590).
 *
 * Everything a rep typed lives in a table of its own; nothing here writes the dialer's columns. Who
 * may open it is the card's rule (ADR-024) widened to the campaigns whose lines a rep staffs — out of
 * scope is a 404, as it is for the card. The tab is in the URL (`?tab=calls`), so a link lands where
 * the reader was.
 */

const TABS = ["timeline", "calls", "sms", "appointments", "tasks", "transcripts", "lists", "notes"] as const;
type Tab = (typeof TABS)[number];

/** Same-phone records listed on the record itself; past that, the contacts list searched by the phone. */
const SIBLINGS_SHOWN = 5;

/**
 * From `lg`, where the record and the activity sit side by side, each column scrolls inside itself and
 * the page never does (ACL-507). As one scroll, the shorter column ran out and left the screen blank
 * beside the longer one: the details' folders against a short timeline, or a long timeline against the
 * facts. Narrower, it is one column and `main` scrolls, as before. `relative` for the reason `main` is
 * (`app-shell.tsx`): the details' `sr-only` labels are placed against the nearest positioned box, and
 * against `main` they escaped the column and scrolled the page again.
 */
const COLUMN_SCROLL = "lg:relative lg:min-h-0 lg:overflow-y-auto scrollbar-quiet";
/** A tab's body is the right column's scroll, so the tabs stay put over it. */
const TAB_PANE = cn(COLUMN_SCROLL, "lg:flex-1");

/** The header's tags and buttons, at the sizes the record's design draws them (ACL-590). */
const HEADER_BADGE = "h-6 rounded-md px-2.5 text-base";
const HEADER_BUTTON = "h-8 [&_svg]:size-4";

/** The attributes worth a line of their own, when the source sent them. The rest (the GHL tags, the
 *  other UTM values, TrustedForm...) stay on the record, off the Source card (ACL-588). */
const SOURCE_ATTRIBUTES: [string, string][] = [
  ["utm_campaign", "UTM campaign"],
];

export function ContactPage() {
  const { user } = useAuth();
  const { leadId } = useParams();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "timeline";
  const { data: contact, isPending, error, refetch } = useContact(leadId);
  // Opened from the contacts list: back to that same list (its search and filters), not the first page.
  const listSearch = placeFromState(useLocation().state)?.search ?? "";
  // Each time the page shows a lead is a visit, on which the rail's "Seen before" tooltip may show
  // itself (ACL-409).
  useSeenBeforePage(contact?.id);
  // What the Send contract dialog listed to fix, marked on the record's fields once it closes, until each
  // is filled in (ACL-541). Kept with the contact it was for: the pager moves on without leaving the page.
  const [asked, setAsked] = React.useState<{ lead: string; fields: ContractAsk } | null>(null);

  if (isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }
  if (error || !contact) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Contact not found"
            description={
              seesCampaigns(user?.role)
                ? "It may belong to another organisation, or to a campaign you do not work."
                : "It may belong to another organisation, or to leads you do not work."
            }
            action={
              <Button variant="outline" asChild>
                <Link to="/contacts">Back to contacts</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={error}
            title="Could not load the contact"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  const tabs = [
    { value: "timeline", label: "Timeline" },
    { value: "calls", label: "Calls" },
    { value: "sms", label: "SMS" },
    { value: "appointments", label: "Appointments" },
    // Tasks is a tab only when core-api has them (ACL-408); a link to it lands on the timeline.
    ...(ownersKnown(contact) ? [{ value: "tasks", label: "Tasks" }] : []),
    { value: "transcripts", label: "Transcripts" },
    // A rep sees no campaign (ACL-652), and these link to a campaign page they cannot open.
    ...(seesCampaigns(user?.role)
      ? [{
          value: "lists",
          label: <>List {contact.campaigns.length ? <span className="ml-1 text-text-subtle">{contact.campaigns.length}</span> : null}</>,
        }]
      : []),
    { value: "notes", label: "Notes" },
  ];
  const current =
    (tab === "tasks" && !ownersKnown(contact)) || (tab === "lists" && !seesCampaigns(user?.role)) ? "timeline" : tab;
  const choose = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === "timeline") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  };

  return (
    // The record takes the room and the activity a narrow panel down the right edge, the whole height of
    // the sheet (ACL-590). Narrower than `lg`, one column: the record, then the activity under it.
    <div className="lg:grid lg:h-full lg:grid-cols-[minmax(0,1fr)_22rem] lg:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_25rem] 2xl:grid-cols-[minmax(0,1fr)_27.25rem]">
      <div className={COLUMN_SCROLL}>
        <Page className="pt-5">
          <div className="mb-5 flex items-center justify-between gap-3">
            <Link to={`/contacts${listSearch}`} className="inline-flex items-center gap-1.5 text-[15px] text-gray-11 hover:text-text">
              <ArrowLeft className="size-4" />
              Contacts
            </Link>
            <ContactPager contactId={contact.id} />
          </div>
          <Header contact={contact} onAsk={(fields) => setAsked(fields ? { lead: contact.id, fields } : null)} />
          <div className="mt-4">
            <Facts contact={contact} ask={asked?.lead === contact.id ? asked.fields : null} />
          </div>
        </Page>
      </div>
      {/* Clipped, not hidden: an overflow that hides makes the panel a scroll box of its own. From `lg`
          the tab's pane is the scroll a day's heading stays at the top of (ACL-508); narrower, the page
          scrolls, and a hidden overflow here would keep the heading from sticking. */}
      {/* The activity reads a size up from the console's body, at 14px (ACL-590): a narrow column of long
          lines reads better a little larger, and as large as the record beside it. */}
      <aside
        aria-label="Activity"
        className={cn(
          "mx-6 mb-6 overflow-clip rounded-lg border border-border bg-surface shadow-xs [--text-sm:var(--text-base)]",
          "lg:m-0 lg:flex lg:min-h-0 lg:flex-col lg:rounded-none lg:border-0 lg:border-l lg:shadow-none",
        )}
      >
        <Tabs className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col" value={current} onValueChange={choose}>
          <OverflowTabsList className="px-4 pt-3" tabs={tabs} value={current} onSelect={choose} />
          <TabsContent value="timeline" className={cn(TAB_PANE, "p-4")}>
            <ContactTimeline contact={contact} composeFocus={params.get("compose") === "1"} />
          </TabsContent>
          <TabsContent value="calls" className={TAB_PANE}>
            <ContactCalls contact={contact} />
          </TabsContent>
          <TabsContent value="sms" className={cn(TAB_PANE, "p-4")}>
            <ContactSmsTab contact={contact} />
          </TabsContent>
          <TabsContent value="appointments" className={cn(TAB_PANE, "p-4")}>
            <ContactAppointmentsTab contact={contact} />
          </TabsContent>
          {ownersKnown(contact) && (
            <TabsContent value="tasks" className={cn(TAB_PANE, "p-4")}>
              <LeadTasks leadId={contact.id} />
            </TabsContent>
          )}
          <TabsContent value="transcripts" className={TAB_PANE}>
            <ContactTranscripts contact={contact} />
          </TabsContent>
          <TabsContent value="lists" className={TAB_PANE}>
            <ContactLists contact={contact} stacked />
          </TabsContent>
          <TabsContent value="notes" className={cn(TAB_PANE, "p-4")}>
            <ContactNotes contact={contact} />
          </TabsContent>
        </Tabs>
      </aside>
    </div>
  );
}

function Header({ contact, onAsk }: { contact: LeadCard; onAsk: (fields: ContractAsk | null) => void }) {
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const admin = atLeast(user?.role, "admin");
  const toast = useToast();
  const now = useNow(30_000);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = React.useState(false);
  const [outcome, setOutcome] = React.useState(false);
  const [booking, setBooking] = React.useState(false);
  const [dnc, setDnc] = React.useState(false);
  const [settingFirm, setSettingFirm] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [blocking, setBlocking] = React.useState(false);
  const [sendingContract, setSendingContract] = React.useState(false);
  // Send contract (ADR-134 in platform) is there only where the deployment has a contract service.
  const contract = useContractCheck(contact.id);
  const contractOn = contract.data !== undefined && contract.data.reason !== "not_set_up";
  // Email (ADR-180 in platform) is there only where the tenant has switched it on; any other
  // refusal (no address, do-not-call...) leaves the button in place, off, with core-api's sentence.
  const emailCompose = useContactEmailCompose(contact.id);
  const emailOn = emailCompose.data !== undefined && emailCompose.data.reason !== "not_set_up";
  // The details panel's own read (the same query): what a wrong value held is marked against it.
  const fields = useContactFields(contact.id);
  const textOf = (key: string) => {
    for (const section of fields.data?.sections ?? []) {
      const found = section.fields.find((f) => f.key === key);
      if (found) return fieldText(found);
    }
    return null;
  };
  /** When the Send contract dialog closes, the values it listed to fix are marked on the record, each
   *  where it stands, and a toast names them (ACL-541); with none, any earlier marks go. */
  const markOnRecord = (found: { code: string | null; fields: ContractField[]; detail: string | null } | null) => {
    const ask = found ? contractAsk(found, textOf) : null;
    onAsk(ask);
    if (!ask || !found) return;
    const said = askedMessage(found.fields, found.code, found.detail);
    toast.error(said.title, said.detail);
  };
  const navigate = useNavigate();
  const record = useContactOutcome();
  // Whether the switch turns this number away now (ADR-159): said beside Do not call, which is
  // the other half of "we do not talk to them" and stops only our own calls and texts.
  const blockable = isOutsideNumber(contact.phone);
  const block = useBlockOn(blockable ? { number: contact.phone, hidden: false } : null);
  const cadence = useLeadCadence(contact.id);
  const onCadence = (cadence.data?.memberships ?? []).filter((m) => m.cadence);
  // The first campaign with a call coming, said by when alone: no cadence words (ACL-575).
  const firstDue = onCadence
    .map((m) => ({ m, due: callDue(m, now, contact.text_first_until ?? null) }))
    .find((row) => row.due !== null);
  const [cadenceAction, setCadenceAction] = React.useState<"pause" | "resume" | "re-enroll" | null>(null);
  const name = contactName(contact);
  const stage = stageText(contact.stage);
  const channelBadge = contactChannelBadge(contact);
  // The clock the dialer calls the lead on: their own zone, else their state's (ACL-495).
  const there = localTime(contact.clock_timezone ?? contact.timezone, now);
  const calling = contact.calling_campaign
    ? contact.campaigns.find((c) => c.campaign_id === contact.calling_campaign?.id) ?? null
    : null;
  const next = calling ? nextText({ dial: calling.dial ?? null, next_at: calling.next_at ?? null }, now) : null;

  const composeNote = () => {
    const p = new URLSearchParams(params);
    p.delete("tab");
    p.set("compose", "1");
    setParams(p, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy", "Copy the address bar instead.");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4.5 gap-y-1.5">
          <h1 className="text-2xl font-semibold leading-8 tracking-tight text-text">{name ?? "No name"}</h1>
          <span className="font-mono text-lg text-gray-11">
            <DialNumberButton number={contact.phone} name={name} lead={contact} />
          </span>
          {contact.firm ? (
            <Badge tone="neutral" className={HEADER_BADGE}>{contact.firm.label}</Badge>
          ) : (
            <Badge tone="warning" className={HEADER_BADGE}>No firm</Badge>
          )}
          {contact.owner && (
            <Badge tone="outline" className={HEADER_BADGE} title="Who the lead belongs to">
              Owner: {contact.owner.name}
            </Badge>
          )}
          {contact.dnc && <Badge tone="danger" className={HEADER_BADGE}>Do not call</Badge>}
          {contact.person_id && (
            <Link to={`/people/${contact.person_id}`} className="text-xs font-medium text-accent-11 hover:underline">
              This person's cases
            </Link>
          )}
          {block && (
            <Badge
              tone="danger"
              className={HEADER_BADGE}
              title={[block.blocked_by ? `Blocked by ${block.blocked_by}` : null, block.note].filter(Boolean).join(". ") || undefined}
            >
              Calls blocked · {untilPhrase(block)}
            </Badge>
          )}
          {channelBadge && <Badge tone="warning" className={HEADER_BADGE} title={channelBadge.title}>{channelBadge.text}</Badge>}
          {/* The record's other records, said where nobody has to scroll for them (ACL-409). */}
          <SeenBeforeTag contact={contact} />
        </div>
        {/* Not shrink-0: on a phone the group is wider than the page, and only a group that may
            shrink lets its buttons wrap instead of running under the edge. */}
        <div className="flex flex-wrap items-center gap-2.5">
          <Button variant="outline" size="sm" className={HEADER_BUTTON} onClick={composeNote}>
            <StickyNote />
            Add note
          </Button>
          {/* Their thread in Conversations: the conversation once they have texted, the record's own
              thread until then (ACL-335). The inbox swaps `?lead=` for `?c=` when there is one. */}
          <Button variant="outline" size="sm" className={HEADER_BUTTON} asChild>
            <Link to={`/conversations/team?lead=${contact.id}`}>
              <MessagesSquare />
              Conversation
            </Link>
          </Button>
          {/* Their email thread: the inbox opens the one core-api would send in, else the record's
              own thread, where the first email starts it. */}
          {emailOn &&
            (emailCompose.data?.can_send ? (
              <Button variant="outline" size="sm" className={HEADER_BUTTON} asChild>
                <Link to={`/conversations/team?lead=${contact.id}&channel=email`}>
                  <Mail />
                  Email
                </Link>
              </Button>
            ) : (
              // A disabled button takes no pointer, so the reason sits on what wraps it.
              <span title={emailCompose.data?.detail ?? undefined}>
                <Button variant="outline" size="sm" className={HEADER_BUTTON} disabled>
                  <Mail />
                  Email
                </Button>
              </span>
            ))}
          {admin && contact.reopen_stage && (
            <Button variant="outline" size="sm" className={HEADER_BUTTON} onClick={() => setReopening(true)}>
              <Undo2 />
              Reopen
            </Button>
          )}
          <Button variant="outline" size="sm" className={HEADER_BUTTON} onClick={() => setOutcome(true)}>
            <PhoneCall />
            Set outcome
          </Button>
          <Button variant="outline" size="sm" className={HEADER_BUTTON} onClick={() => setBooking(true)}>
            <CalendarClock />
            Book a callback
          </Button>
          {contractOn && (
            <Button
              variant="outline"
              size="sm"
              className={HEADER_BUTTON}
              onClick={() => {
                void contract.refetch();
                setSendingContract(true);
              }}
            >
              <FileSignature />
              Send contract
            </Button>
          )}
          {!contact.dnc && (
            <Button variant="outline" size="sm" className={HEADER_BUTTON} onClick={() => setDnc(true)}>
              <Ban />
              Do not call
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* The details are changed where they are read, on the record's panel. The drawer stays
                  here for what a tenant's form does not hold: how the contact wants to be reached
                  (ACL-343, ACL-344), and for a core-api that cannot send the fields. */}
              <DropdownMenuItem onSelect={() => setEditing(true)}>
                <Pencil />
                Edit contact
              </DropdownMenuItem>
              {supervisor && (
                <DropdownMenuItem onSelect={() => setSettingFirm(true)}>
                  <Tag />
                  Set firm
                </DropdownMenuItem>
              )}
              {supervisor && onCadence.some((m) => m.held_by !== "cadence:paused" && !["done", "dnc", "exhausted", "retired", "blocked"].includes(m.state)) && (
                <DropdownMenuItem onSelect={() => setCadenceAction("pause")}>
                  <Pause />
                  Pause follow up
                </DropdownMenuItem>
              )}
              {supervisor && onCadence.some((m) => m.held_by === "cadence:paused") && (
                <DropdownMenuItem onSelect={() => setCadenceAction("resume")}>
                  <Play />
                  Resume follow up
                </DropdownMenuItem>
              )}
              {supervisor && !contact.dnc && onCadence.some((m) => ["exhausted", "done", "retired"].includes(m.state)) && (
                <DropdownMenuItem onSelect={() => setCadenceAction("re-enroll")}>
                  <RotateCcw />
                  Start follow up again
                </DropdownMenuItem>
              )}
              {blockable && (
                <DropdownMenuItem onSelect={() => setBlocking(true)}>
                  <PhoneOff />
                  {block ? "Calls blocked" : "Block caller"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => void copyLink()}>
                <Link2 />
                Copy link
              </DropdownMenuItem>
              {admin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onSelect={() => setDeleting(true)}>
                    <Trash2 />
                    Delete contact
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Where the lead stands, on one quiet line under the name: words in one grey, the stage's colour a
          dot beside it. */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-11">
        <span className="inline-flex items-center gap-1.5 font-medium text-text" title={stage.description}>
          <ToneDot tone={stage.tone} />
          {stage.label}
        </span>
        {contact.stage_since && <span>since {relativeTime(contact.stage_since)}</span>}
        <span aria-hidden>·</span>
        <span>
          {[
            contact.state ? `${contact.state}` : null,
            there ? `${there} there` : null,
            languageName(contact.language),
          ].filter(Boolean).join(" · ") || "No location on file"}
        </span>
        {/* When they are called next, for an admin and up (ACL-642). */}
        {!seesNextCall(user?.role) ? null : firstDue?.due ? (
          <>
            <span aria-hidden>·</span>
            <span title={firstDue.due.at ? formatDateTime(firstDue.due.at) : undefined}>
              {firstDue.due.kind === "due" ? `Next call: ${inSentence(firstDue.due.text)}` : firstDue.due.text} on {firstDue.m.campaign_name}
            </span>
          </>
        ) : calling ? (
          <>
            <span aria-hidden>·</span>
            <span>
              Next: {next ? `${inSentence(next)} on ${contact.calling_campaign?.name}` : `${inSentence(dialText(calling.dial ?? null, calling.outcome_rep).label)} on ${contact.calling_campaign?.name}`}
            </span>
          </>
        ) : null}
      </div>

      <ContactFormDialog open={editing} onOpenChange={setEditing} contact={contact} />
      <ContactOutcomeDialog open={outcome} onOpenChange={setOutcome} contact={contact} />
      <BookCallbackDialog open={booking} onOpenChange={setBooking} contact={contact} />
      <SetFirmDialog open={settingFirm} onOpenChange={setSettingFirm} contact={contact} />
      <CadenceActionDialog action={cadenceAction} onClose={() => setCadenceAction(null)} contact={contact} />
      <ReopenDialog open={reopening} onOpenChange={setReopening} contact={contact} />
      <BlockCallerDialog
        open={blocking}
        onOpenChange={setBlocking}
        target={{ number: contact.phone, hidden: false, leadId: contact.id, name }}
      />
      <SendContractDialog
        open={sendingContract}
        onOpenChange={setSendingContract}
        contact={contact}
        check={contract.data}
        checking={contract.isFetching}
        onClosed={markOnRecord}
      />
      <DeleteLeadDialog
        open={deleting}
        onOpenChange={setDeleting}
        contact={contact}
        onDeleted={() => navigate("/contacts", { replace: true })}
      />
      <ConfirmDialog
        open={dnc}
        onOpenChange={setDnc}
        title="Mark as do not call?"
        description="Every record with this phone is marked, every list drops them, and an admin cannot undo it. The outcome is recorded as Do not call."
        confirmLabel="Mark as do not call"
        destructive
        loading={record.isPending}
        onConfirm={() =>
          record.mutate(
            { leadId: contact.id, disposition: "dnc" },
            {
              onSuccess: () => {
                setDnc(false);
                toast.success("Marked as do not call");
              },
              onError: (e) =>
                toast.error("Could not mark as do not call", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </div>
  );
}

/** An admin takes the lead out of its exit stage (ADR-076). From the board, `into` is the column the
 *  card was dropped on (ADR-078); else the stage it had before. */
export function ReopenDialog({
  open, onOpenChange, contact, into = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
  into?: string | null;
}) {
  const toast = useToast();
  const reopen = useReopenLead();
  const [stage, setStage] = React.useState<string>("");
  const [reason, setReason] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setStage(into ?? contact.reopen_stage ?? "new");
    setReason("");
    setProblem(null);
  }, [open, into, contact.reopen_stage]);
  const exit = stageText(contact.stage).label;
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say why the lead is reopened.");
      return;
    }
    reopen.mutate(
      { leadId: contact.id, stage, reason: reason.trim() },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          toast.success("Reopened", reopenSummary(result));
        },
        onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not reopen. Please try again."),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reopen this contact?</DialogTitle>
          <DialogDescription>
            They leave {exit} for the stage you pick, and go back on the dial lists {exit} took them off:
            each one carries on where it left off. Contacts with the same phone that left with them come
            back too. The reason is kept in the history.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Reopen into" htmlFor="reopen-stage" hint={stageText(stage).description}>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger id="reopen-stage" aria-label="Reopen into">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reopenStages().map((s) => (
                  <SelectItem key={s} value={s} description={s === contact.reopen_stage ? "Where they were before" : undefined}>
                    {stageText(s).label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason" htmlFor="reopen-reason" required error={problem ?? undefined}>
            <Input
              id="reopen-reason"
              value={reason}
              maxLength={500}
              invalid={Boolean(problem)}
              placeholder="e.g. marked Not qualified by mistake"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setReason(e.target.value);
                setProblem(null);
              }}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button loading={reopen.isPending} onClick={submit}>Reopen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetFirmDialog({
  open, onOpenChange, contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const firms = useFirms({ enabled: open });
  const update = useUpdateContact();
  const [firmId, setFirmId] = React.useState<string>("");
  React.useEffect(() => {
    if (open) setFirmId(contact.firm ? String(contact.firm.id) : "");
  }, [open, contact.firm]);
  const chosen = firmId ? Number(firmId) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Set firm</DialogTitle>
          <DialogDescription>
            The firm this contact belongs to. It writes the firm tag on the record and leaves a note saying who set it.
            {contact.state && ` The firms that take cases in ${contact.state} come first, the preferred one at the top.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Firm" htmlFor="set-firm">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger id="set-firm" aria-label="Firm">
                <SelectValue placeholder="Choose a firm…" />
              </SelectTrigger>
              <SelectContent>
                <FirmOptions firms={firms.data ?? []} state={contact.state} />
              </SelectContent>
            </Select>
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            loading={update.isPending}
            disabled={chosen === null || chosen === (contact.firm?.id ?? null)}
            onClick={() =>
              chosen !== null &&
              update.mutate(
                { id: contact.id, firm_id: chosen },
                {
                  onSuccess: () => {
                    toast.success("Firm set");
                    onOpenChange(false);
                  },
                  onError: (e) => toast.error("Could not set the firm", e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            Set firm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Pause, resume or start the follow-up again (ADR-066). A pause is a hold on the lead's rows,
 *  written at once; starting again is a request the dialer applies at its next sweep. */
function CadenceActionDialog({
  action, onClose, contact,
}: {
  action: "pause" | "resume" | "re-enroll" | null;
  onClose: () => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const act = useLeadCadenceAction();
  const [reason, setReason] = React.useState("");
  React.useEffect(() => {
    if (action) setReason("");
  }, [action]);
  const words = {
    pause: {
      title: "Pause the follow up?",
      description: "No cadence calls this lead until somebody resumes. A reason is kept on the record.",
      confirm: "Pause",
    },
    resume: {
      title: "Resume the follow up?",
      description: "The lead is called again from where the cadence left it.",
      confirm: "Resume",
    },
    "re-enroll": {
      title: "Start the follow up again?",
      description: "The lead starts the cadence from the top, as a fresh lead, at the dialer's next sweep. Refused for a lead marked do not call, or a signed one.",
      confirm: "Start again",
    },
  } as const;
  const w = action ? words[action] : words.pause;
  return (
    <Dialog open={action !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{w.title}</DialogTitle>
          <DialogDescription>{w.description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Reason" htmlFor="cadence-action-reason" hint="Optional. Kept with the request and on the record.">
            <Input value={reason} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)} maxLength={500} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={act.isPending}
            onClick={() =>
              action &&
              act.mutate(
                { leadId: contact.id, action, reason: reason.trim() || null, idempotency_key: `${action}:${contact.id}:${Date.now()}` },
                {
                  onSuccess: (result) => {
                    onClose();
                    toast.success(
                      result.affected === 0 ? "Nothing to change" : w.confirm,
                      result.affected === 0
                        ? "No list row of this lead was in a state that action applies to."
                        : `${result.affected} list row${result.affected === 1 ? "" : "s"}.`,
                    );
                  },
                  onError: (e) => toast.error(`Could not ${w.confirm.toLowerCase()}`, e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            {w.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Facts({ contact, ask }: { contact: LeadCard; ask: ContractAsk | null }) {
  const { user } = useAuth();
  const cadence = useLeadCadence(contact.id);
  const accident = useLeadAccident(contact.id, { poll: true });
  const { byPhone } = accidentSections(accident.data);
  const now = useNow(30_000);
  const attributes = contact.attributes ?? {};
  const shown = SOURCE_ATTRIBUTES.filter(([key]) => attributes[key] !== undefined && attributes[key] !== null && attributes[key] !== "");
  const source = sourceText(contact.source);
  return (
    // In the order the record's design reads (ACL-590): who they are, the accident, then the details.
    <div className="space-y-6">
      {/* Who to call when the firm cannot reach them, first for an MIA lead (ACL-382). */}
      <EmergencyContactCard contact={contact} />
      {seesNextCall(user?.role) && <NextCallCard contact={contact} cadence={cadence.data} now={now} />}
      <IdentityCard contact={contact} />
      {/* The supervisor flags an intake rule raised; nothing at all when there are none (ACL-341). */}
      <ContactReviewFlags leadId={contact.id} />
      <AccidentCard leadId={contact.id} source={contact} />
      {/* The details panel: the contact form's folders with this contact's answers, changed in place
          (ACL-255, ACL-405). */}
      <ContactDetailsPanel contact={contact} ask={ask} wide />
      {/* Which firm and which lists, and no more: the attempts, the last outcome and the next call are
          the List tab's and the Next call card's (ACL-588), the card an admin's alone (ACL-642). */}
      <Card>
        <RecordCardHeader title="Firm and lists" />
        <dl className={RECORD_LIST}>
          <RecordRow label="Firm">
            {contact.firm ? (
              contact.firm.label
            ) : (
              <span className="text-warning-11" title="Set one, or the next import resolves its tag">No firm</span>
            )}
          </RecordRow>
          {seesCampaigns(user?.role) && <RecordRow label={contact.campaigns.length === 1 ? "List" : "Lists"}>
            {contact.campaigns.length === 0 ? (
              <span className="text-text-muted">On no campaign's list</span>
            ) : (
              <ul className="space-y-2">
                {contact.campaigns.map((m) => {
                  const dial = dialText(m.dial ?? null, m.outcome_rep);
                  return (
                    <li key={m.campaign_id}>
                      <Link to={`/campaigns/${m.campaign_id}?tab=lists`} className="hover:underline">
                        {m.campaign_name ?? "Deleted campaign"}
                      </Link>
                      <Badge tone={dial.tone} size="sm" className="ml-2 align-middle" title={dial.description}>{dial.label}</Badge>
                      {m.list_name && <div className="mt-0.5 text-xs text-text-muted">list: {m.list_name}</div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </RecordRow>}
        </dl>
      </Card>
      {/* What became of each Send contract (ACL-427); nothing until the first press. */}
      <ContractHistoryCard key={contact.id} leadId={contact.id} />
      {contact.siblings && contact.siblings.length > 0 && (
        <Card id={SAME_PHONE_CARD_ID} className="scroll-mt-4">
          <RecordCardHeader
            title="Same phone"
            description={SAME_PHONE_CAVEAT}
            action={
              <Badge tone="neutral">
                {contact.siblings.length} {contact.siblings.length === 1 ? "record" : "records"}
              </Badge>
            }
          />
          <div className="px-5">
            <SamePhoneRecords
              siblings={contact.siblings}
              phone={contact.phone}
              limit={SIBLINGS_SHOWN}
              caveat={false}
              footer={(sibling) => <AccidentMark leadId={contact.id} record={byPhone.get(sibling.id)} />}
            />
          </div>
        </Card>
      )}
      <Card>
        <RecordCardHeader title="Source" />
        <dl className={RECORD_LIST}>
          {/* In a person's words, as the board says it: `csv:7` is "CSV import". The sender's own id
              (`source_ref`) is the import's to match on, not anyone's to read (ACL-588). */}
          <RecordRow label="Source">
            {source.label}
            {source.detail && <span className="text-text-muted"> · {source.detail}</span>}
          </RecordRow>
          {shown.map(([key, label]) => (
            <RecordRow key={key} label={label}>
              {Array.isArray(attributes[key]) ? (attributes[key] as unknown[]).map(String).join(", ") : String(attributes[key])}
            </RecordRow>
          ))}
          <RecordRow label="Created">{formatDateTime(contact.created_at)}</RecordRow>
        </dl>
      </Card>
    </div>
  );
}

/** A card's head on the record (ACL-590): a larger title and a little more room than a list's card. */
function RecordCardHeader({ className, ...props }: React.ComponentProps<typeof CardHeader>) {
  return <CardHeader className={cn("min-h-15 items-center px-5 py-3.5 [&_h2]:text-lg", className)} {...props} />;
}

/** The record's label and value rows (ACL-590): the label in a column of its own, a line between rows. */
const RECORD_LIST = "divide-y divide-border px-5 pb-1";

function RecordRow({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)] items-baseline py-3">
      <dt className="text-base leading-5 text-gray-11">{label}</dt>
      <dd className="min-w-0 break-words text-[15px] leading-5 text-text">{children}</dd>
    </div>
  );
}

/**
 * Who the contact is and how to reach them, two columns across the record (ACL-590): the phone dials, the
 * e-mail copies, and Edit opens the contact's drawer, as Edit contact does. The first folder's short
 * answers follow as tags, so anyone reads the case in seconds without opening a folder.
 */
function IdentityCard({ contact }: { contact: LeadCard }) {
  const toast = useToast();
  const fields = useContactFields(contact.id);
  const [editing, setEditing] = React.useState(false);
  const name = contactName(contact);
  const none = <span className="text-text-subtle">Not set</span>;
  const zone = contact.timezone ? (US_TIMEZONES.find((tz) => tz.zone === contact.timezone)?.label ?? contact.timezone) : null;
  const answers = fields.data ? keyAnswers(shownSections(fields.data.sections, true)) : [];
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contact.email ?? "");
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy", "Select the address and copy it instead.");
    }
  };
  return (
    <Card className="@container">
      <RecordCardHeader
        title="Identity"
        action={
          <Button variant="outline" className="text-base" onClick={() => setEditing(true)}>
            <Pencil />
            Edit
          </Button>
        }
      />
      <div className="grid px-5 pb-4 @xl:grid-cols-2 @xl:gap-x-10">
        <dl className="divide-y divide-border">
          <IdentityRow label="Name">{name ?? none}</IdentityRow>
          <IdentityRow label="Phone">
            <span className="font-mono font-medium">
              <DialNumberButton number={contact.phone} name={name} />
            </span>
          </IdentityRow>
          <IdentityRow label="Email">
            {contact.email ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="min-w-0 break-words">
                  {/* Broken after the @ when it must break, not wherever the line ends. */}
                  {contact.email.slice(0, contact.email.indexOf("@") + 1)}
                  <wbr />
                  {contact.email.slice(contact.email.indexOf("@") + 1)}
                </span>
                <Button variant="ghost" size="icon-sm" className="-my-1 shrink-0" aria-label="Copy the email" title="Copy" onClick={() => void copyEmail()}>
                  <Copy />
                </Button>
              </span>
            ) : (
              none
            )}
          </IdentityRow>
          <IdentityRow label="Language">{languageName(contact.language) ?? none}</IdentityRow>
        </dl>
        <dl className="divide-y divide-border border-t border-border @xl:border-t-0">
          <IdentityRow label="Reach by">
            {contact.contact_channel !== undefined ? (
              <>
                {contactChannelLabel(contactChannelOf(contact))}
                {textFirstEnds(contact) && <span className="text-text-muted"> · calls start {textFirstEnds(contact)}</span>}
              </>
            ) : (
              none
            )}
          </IdentityRow>
          <IdentityRow label="Timezone">{zone ?? <span className="text-gray-11">From the state</span>}</IdentityRow>
          <IdentityRow label="State">{contact.state ?? none}</IdentityRow>
          {contact.calling_window !== undefined && (
            <IdentityRow label="Calling window">
              <CallingWindowLine contact={contact} />
            </IdentityRow>
          )}
        </dl>
      </div>
      {answers.length > 0 && (
        <div className="border-t border-border px-5 py-3">
          <div className="pb-2 text-sm text-text-muted">At a glance</div>
          <ul className="flex flex-wrap gap-1.5">
            {answers.map((answer) => (
              <li
                key={answer.key}
                title={answer.title}
                className="rounded-full border border-border bg-surface-sunken px-2.5 py-0.5 text-sm text-text"
              >
                {answer.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      <ContactFormDialog open={editing} onOpenChange={setEditing} contact={contact} />
    </Card>
  );
}

function IdentityRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-11 grid-cols-[130px_minmax(0,1fr)] items-center py-2.5">
      <dt className="text-base leading-5 text-gray-11">{label}</dt>
      <dd className="min-w-0 break-words text-[15px] leading-5 text-text">{children}</dd>
    </div>
  );
}

/** The lists the contact is on. A supervisor adds it to another from here (ADR-125): the button
 *  draws nothing for a rep, who is then only told where the contact stands. `stacked` is the record's
 *  narrow panel (ACL-590): a short block of lines per list, where a table would run out of room. */
export function ContactLists({ contact, stacked = false }: { contact: LeadCard; stacked?: boolean }) {
  if (contact.campaigns.length === 0) {
    return (
      <EmptyState
        title="On no campaign's list"
        description="No campaign's dialer calls this contact. A supervisor can add it to a campaign's list."
        action={<AddToCampaign contact={contact} />}
      />
    );
  }
  return (
    <>
      <div className="flex justify-end border-b border-border px-4 py-2 empty:hidden">
        <AddToCampaign contact={contact} />
      </div>
      {stacked ? <ContactListLines contact={contact} /> : <ContactListRows contact={contact} />}
    </>
  );
}

/** The table's columns, a list to a block: the campaign and how its dialer holds the lead, then the
 *  attempts and the last outcome, then what comes next. */
function ContactListLines({ contact }: { contact: LeadCard }) {
  const look = useOutcomeLook(null, contact.campaigns.some((m) => m.last_disposition !== null));
  return (
    <ul className="divide-y divide-border">
      {contact.campaigns.map((m) => {
        const dial = dialText(m.dial ?? null, m.outcome_rep);
        const next = nextText({ dial: m.dial ?? null, next_at: m.next_at ?? null });
        const list = m.list_name ?? (m.list_id ? `List ${m.list_id}` : null);
        return (
          <li key={m.campaign_id} className="space-y-1 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <Link to={`/campaigns/${m.campaign_id}?tab=lists`} className="min-w-0 truncate text-sm font-medium text-text hover:underline">
                {m.campaign_name ?? "Deleted campaign"}
              </Link>
              <Badge tone={dial.tone} size="sm" title={dial.description} className="shrink-0">{dial.label}</Badge>
            </div>
            {m.dial === "held" && m.hold_reason && (
              <p className="text-xs text-text-muted">{dialDetail({ dial: m.dial, membership: m })}</p>
            )}
            <p className="text-xs text-text-muted">
              Attempts{" "}
              <span className="tabular-nums">
                {m.attempts}
                {m.max_attempts ? ` of ${m.max_attempts}` : null}
              </span>
              {" · "}Last:{" "}
              {m.last_disposition ? (
                <RecordedOutcome slug={m.last_disposition} look={look} className="align-middle" />
              ) : (
                "none yet"
              )}
              {m.last_attempt_at && <span className="text-text-subtle"> · {relativeTime(m.last_attempt_at)}</span>}
            </p>
            <p className="text-xs text-text-muted">
              {[
                next ? `Next: ${next.toLowerCase()}` : null,
                m.retire_at ? `Leaves the list ${formatDateTime(m.retire_at)}` : null,
                list ? `List: ${list}` : null,
              ].filter(Boolean).join(" · ") || "Nothing scheduled"}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

function ContactListRows({ contact }: { contact: LeadCard }) {
  const look = useOutcomeLook(null, contact.campaigns.some((m) => m.last_disposition !== null));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Campaign</TableHead>
          <TableHead>Dial</TableHead>
          <TableHead>Attempts</TableHead>
          <TableHead>Last outcome</TableHead>
          <TableHead>Next</TableHead>
          <TableHead>Leaves the list</TableHead>
          <TableHead>List</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contact.campaigns.map((m) => {
          const dial = dialText(m.dial ?? null, m.outcome_rep);
          const next = nextText({ dial: m.dial ?? null, next_at: m.next_at ?? null });
          return (
            <TableRow key={m.campaign_id}>
              <TableCell>
                <Link to={`/campaigns/${m.campaign_id}?tab=lists`} className="hover:underline">
                  {m.campaign_name ?? "Deleted campaign"}
                </Link>
              </TableCell>
              <TableCell>
                <Badge tone={dial.tone} size="sm" title={dial.description}>{dial.label}</Badge>
                {m.dial === "held" && m.hold_reason && <span className="ml-1 text-xs text-text-muted">{dialDetail({ dial: m.dial, membership: m })}</span>}
              </TableCell>
              <TableCell className="tabular-nums">
                {m.attempts}
                {m.max_attempts ? <span className="text-text-subtle"> of {m.max_attempts}</span> : null}
              </TableCell>
              <TableCell className="text-xs">
                {m.last_disposition ? (
                  <RecordedOutcome slug={m.last_disposition} look={look} className="align-middle" />
                ) : (
                  <span className="text-text-subtle">—</span>
                )}
                {m.last_attempt_at && <span className="text-text-subtle"> · {relativeTime(m.last_attempt_at)}</span>}
              </TableCell>
              <TableCell className="text-xs text-text-muted">{next ?? "—"}</TableCell>
              <TableCell className="text-xs text-text-muted">{m.retire_at ? formatDateTime(m.retire_at) : "—"}</TableCell>
              <TableCell className="text-xs text-text-muted">{m.list_name ?? (m.list_id ? `List ${m.list_id}` : "—")}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** The contact's call legs, newest first. A leg is a short block of lines, not a table's row: the tab is
 *  a narrow panel's (ACL-590). A press opens the call, as a row of the call history does. */
function ContactCalls({ contact }: { contact: LeadCard }) {
  const [page, setPage] = React.useState(0);
  const [openId, setOpenId] = React.useState<number | undefined>();
  const list = useContactCalls(contact.id, page);
  const rows = list.data?.items ?? [];
  return (
    <>
      {list.isPending ? (
        <TableSkeleton cols={2} rows={4} />
      ) : list.error ? (
        <ErrorState error={list.error} title="Could not load the calls" />
      ) : rows.length === 0 ? (
        <EmptyState title="No calls yet" description="Every leg the switch finished with this lead on it appears here." />
      ) : (
        <>
          <ul className="divide-y divide-border">
            {rows.map((call) => (
              <CallLine key={call.id} call={call} onOpen={() => setOpenId(call.id)} />
            ))}
          </ul>
          <CallPager page={page} count={rows.length} total={list.data?.total ?? null} onChange={setPage} busy={list.isFetching} pageSize={CALLS_PAGE_SIZE} />
        </>
      )}
      <CallDetailDialog
        id={openId}
        onClose={() => setOpenId(undefined)}
        onOpenLeg={setOpenId}
      />
    </>
  );
}

function CallLine({ call, onOpen }: { call: Cdr; onOpen: () => void }) {
  const { user } = useAuth();
  const outcome = callOutcome(call);
  const type = knownCallType(call.kind);
  return (
    <li>
      <button
        type="button"
        className="block w-full px-4 py-3 text-left transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:-outline-offset-2"
        aria-label={`Call at ${formatDateTime(call.start_time)}`}
        onClick={onOpen}
      >
        <span className="flex items-center justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-text">
            <DirectionIcon direction={call.direction} />
            <span className="truncate">{formatDateTime(call.start_time)}</span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5">
            <CallMarkBadge call={call} />
            <Badge tone={outcome.tone} size="sm">{outcome.label}</Badge>
          </span>
        </span>
        <span className="mt-1 flex items-center gap-1.5 text-xs text-text-muted">
          {type && <CallTypeChip type={type} size="sm" short />}
          {seesCampaigns(user?.role) && <span className="min-w-0 truncate">{call.campaign_name ?? "No campaign"}</span>}
          {talkShown(call) && (
            <>
              <span aria-hidden>·</span>
              <span className="shrink-0 font-mono">{formatDuration(call.billsec_seconds)}</span>
            </>
          )}
          <CallMedia call={call} />
        </span>
        <span className="mt-0.5 block truncate font-mono text-xs text-text-subtle">
          {call.caller_number ? formatPhoneNumber(call.caller_number) : "Unknown"}
          <span className="font-sans"> → </span>
          {call.callee_number ? formatPhoneNumber(call.callee_number) : "Unknown"}
        </span>
      </button>
    </li>
  );
}

/** A recorded or transcribed call says so beside its talk time, as the call history's media column does. */
function CallMedia({ call }: { call: Pick<Cdr, "has_recording" | "has_transcript"> }) {
  if (!call.has_recording && !call.has_transcript) return null;
  return (
    <span className="ml-auto inline-flex shrink-0 items-center gap-1">
      {call.has_recording && (
        <Tooltip content="This call was recorded. Open it to listen.">
          <Mic className="size-3.5 text-accent-11" aria-label="Recorded" />
        </Tooltip>
      )}
      {call.has_transcript && (
        <Tooltip content="This call was transcribed. Open it to read.">
          <FileText className="size-3.5 text-success-11" aria-label="Transcribed" />
        </Tooltip>
      )}
    </span>
  );
}
